"use client";

import React from "react";
import { LuChevronLeft, LuChevronRight, LuArrowUp, LuArrowDown, LuCheck, LuChevronDown } from "react-icons/lu";
import { cn } from "../../lib/utils";
import { ICommonTableProps, TableSelectionMode } from "./types";
import { DEFAULT_PAGE_SIZES, TABLE_STYLES } from "./constants";

function generatePageNumbers(currentPage: number, totalPages: number): (number | string)[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages: (number | string)[] = [1];

  let start = Math.max(2, currentPage - 1);
  let end = Math.min(totalPages - 1, currentPage + 1);

  if (currentPage <= 3) {
    start = 2;
    end = 4;
  }

  if (currentPage >= totalPages - 2) {
    start = totalPages - 3;
    end = totalPages - 1;
  }

  if (start > 2) {
    pages.push("...");
  }

  for (let i = start; i <= end; i++) {
    pages.push(i);
  }

  if (end < totalPages - 1) {
    pages.push("...");
  }

  pages.push(totalPages);
  return pages;
}

function CommonTable<T>({
  columns,
  data,
  page = 1,
  pageSize = data?.length ? data.length : 25,
  total = data?.length ?? 0,
  onPageSizeChange,
  onPageChange,
  isLoading,
  onRowClick,
  rowMenu,
  selectionMode = TableSelectionMode.NONE,
  selectedRows,
  onSelectionChange,
  errorMessage,
  sortKey,
  sortDirection,
  onSortChange,
  hideFooter = false,
  loadingRowSkeleton,
  getRowId,
  pageSizeOptions = DEFAULT_PAGE_SIZES,
  emptyMessage = "No data to display",
  loadingMessage = "Loading...",
  showLabel = "Show",
  rowsPerPageLabel = "rows per page",
}: ICommonTableProps<T>) {
  const getItemId = (row: T, index: number): string | number => {
    if (getRowId) return getRowId(row);
    if ((row as Record<string, unknown>).id !== undefined) return (row as Record<string, unknown>).id as string | number;
    return index;
  };

  const isSelected = (row: T, index: number) => {
    const rowId = getItemId(row, index);
    return selectedRows?.some((r, i) => getItemId(r, i) === rowId);
  };

  const to = total > 0 ? Math.min(page * pageSize, total) : 0;
  const totalPages = Math.ceil(total / pageSize);

  if (isLoading) {
    return (
      <div className={cn(TABLE_STYLES.container, "border animate-pulse")}>
        <div className={TABLE_STYLES.tableWrapper}>
          <table className={TABLE_STYLES.table}>
            <thead>
              <tr className="bg-gray-50">
                {selectionMode !== TableSelectionMode.NONE && <th className="px-4 py-2 w-10"></th>}
                {columns.map((col) => (
                  <th
                    key={col.key}
                    className={cn(
                      TABLE_STYLES.headerCell,
                      col.className,
                      col.sortable && TABLE_STYLES.headerCellSortable
                    )}
                  >
                    <div className="flex items-center">{col.header}</div>
                  </th>
                ))}
                <th className={TABLE_STYLES.headerCell}></th>
              </tr>
            </thead>
            <tbody>
              {[...Array(5)].map((_, i) =>
                loadingRowSkeleton ? (
                  <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                    {loadingRowSkeleton}
                  </tr>
                ) : (
                  <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                    {selectionMode !== TableSelectionMode.NONE && (
                      <td className="px-4 py-2 whitespace-nowrap w-10">
                        <div className="h-4 bg-gray-200 rounded w-4" />
                      </td>
                    )}
                    {columns.map((col) => (
                      <td key={col.key} className="px-4 py-2 whitespace-nowrap">
                        <div className={TABLE_STYLES.skeletonRow} />
                      </td>
                    ))}
                    <td className="px-4 py-2 whitespace-nowrap flex">
                      <div className="h-4 bg-gray-200 rounded w-6 ml-auto" />
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        </div>

        {!hideFooter && (
          <div className={cn(TABLE_STYLES.footer, "border-t bg-gray-50")}>
            <span className={TABLE_STYLES.loadingText}>{loadingMessage}</span>
            <div className="flex gap-1">
              <div className="px-2 py-1 rounded bg-gray-200" style={{ width: 24, height: 24 }} />
              <div className="px-2 py-1 rounded bg-gray-200" style={{ width: 24, height: 24 }} />
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={TABLE_STYLES.container}>
      <div className={TABLE_STYLES.tableWrapper}>
        <table className={TABLE_STYLES.table}>
          <thead>
            {data.length === 0 || errorMessage ? (
              <tr className={TABLE_STYLES.headerRow}>
                <th className={cn(TABLE_STYLES.headerCell, "rounded-lg")}></th>
              </tr>
            ) : (
              <tr className={TABLE_STYLES.headerRow}>
                {selectionMode === TableSelectionMode.MULTIPLE && (
                  <th className={cn("px-4 py-2 w-10", TABLE_STYLES.headerCellFirst)}>
                    <input
                      type="checkbox"
                      checked={data.length > 0 && data.every((row, i) => isSelected(row, i))}
                      onChange={(e) => {
                        if (onSelectionChange) onSelectionChange(e.target.checked ? data : []);
                      }}
                      className="rounded border-gray-300"
                    />
                  </th>
                )}

                {selectionMode === TableSelectionMode.SINGLE && (
                  <th className={cn("px-4 py-2 w-10", TABLE_STYLES.headerCellFirst)}></th>
                )}

                {columns.map((col, index) => (
                  <th
                    key={col.key}
                    onClick={() => {
                      if (col.sortable && onSortChange) {
                        const newDirection = sortKey === col.key && sortDirection === "asc" ? "desc" : "asc";
                        onSortChange(col.key, newDirection);
                      }
                    }}
                    className={cn(
                      TABLE_STYLES.headerCell,
                      col.className,
                      index === 0 && selectionMode === TableSelectionMode.NONE && TABLE_STYLES.headerCellFirst,
                      col.sortable && TABLE_STYLES.headerCellSortable,
                      index === columns.length - 1 &&
                        !rowMenu &&
                        selectionMode === TableSelectionMode.NONE &&
                        TABLE_STYLES.headerCellLast
                    )}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span>{col.header}</span>
                      {col.sortable && sortKey === col.key && (
                        <span className="flex items-center text-gray-500">
                          {sortDirection === "asc" ? (
                            <LuArrowUp className="h-4 w-4" />
                          ) : (
                            <LuArrowDown className="h-4 w-4" />
                          )}
                        </span>
                      )}
                    </div>
                  </th>
                ))}

                {rowMenu && (
                  <th
                    className={cn(
                      TABLE_STYLES.headerCell,
                      selectionMode === TableSelectionMode.NONE && TABLE_STYLES.headerCellLast
                    )}
                  ></th>
                )}
              </tr>
            )}
          </thead>
          <tbody>
            {data.length === 0 || errorMessage ? (
              <tr>
                <td
                  colSpan={columns.length + 1}
                  className={errorMessage ? TABLE_STYLES.errorState : TABLE_STYLES.emptyState}
                >
                  {errorMessage || emptyMessage}
                </td>
              </tr>
            ) : (
              data.map((row, i) => {
                const isRowClickable = !!onRowClick;
                const handleRowClick = () => {
                  if (selectionMode !== TableSelectionMode.NONE) {
                    onSelectionChange?.([row]);
                  } else if (isRowClickable) {
                    onRowClick?.(row);
                  }
                };

                return (
                  <tr
                    key={getItemId(row, i)}
                    onClick={handleRowClick}
                    className={cn(
                      TABLE_STYLES.bodyRow,
                      (selectionMode !== TableSelectionMode.NONE || isRowClickable) &&
                        TABLE_STYLES.bodyRowClickable
                    )}
                  >
                    {selectionMode !== TableSelectionMode.NONE && (
                      <td className="px-4 py-4 w-10">
                        {selectionMode === TableSelectionMode.MULTIPLE && (
                          <input
                            type="checkbox"
                            checked={isSelected(row, i)}
                            onChange={(e) => {
                              e.stopPropagation();
                              if (!onSelectionChange) return;
                              const rowId = getItemId(row, i);
                              onSelectionChange(
                                e.target.checked
                                  ? [...(selectedRows ?? []), row]
                                  : selectedRows?.filter((r, ri) => getItemId(r, ri) !== rowId) ?? []
                              );
                            }}
                            className="rounded border-gray-300"
                          />
                        )}
                        {selectionMode === TableSelectionMode.SINGLE && isSelected(row, i) && (
                          <LuCheck className="h-4 w-4 text-blue-600" />
                        )}
                      </td>
                    )}

                    {columns.map((col) => (
                      <td
                        key={col.key}
                        className={cn(
                          TABLE_STYLES.bodyCell,
                          !col.className?.includes("whitespace-") && "whitespace-nowrap",
                          col.className
                        )}
                      >
                        {col.render ? col.render(row) : (row as Record<string, unknown>)[col.key] as React.ReactNode}
                      </td>
                    ))}
                    {rowMenu && (
                      <td className="px-4 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end space-x-2">
                          {rowMenu(row)}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      {!hideFooter && (
        <div className={TABLE_STYLES.footer}>
          <div className="flex items-center space-x-2 text-xs text-gray-500">
            <span className="text-[#929292] text-xs font-light">{showLabel}</span>
            <div className="relative">
              <select
                value={pageSize}
                onChange={(e) => onPageSizeChange?.(Number(e.target.value))}
                className={TABLE_STYLES.pageSizeSelect}
              >
                {pageSizeOptions.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
              <LuChevronDown className="absolute right-1 top-1/2 -translate-y-1/2 h-4 w-4 text-[#0079D9] pointer-events-none" />
            </div>
            <span className="hidden sm:block lowercase text-[#929292] text-xs font-light whitespace-nowrap">
              {rowsPerPageLabel}
            </span>
          </div>
          <div className={TABLE_STYLES.pagination}>
            <button
              disabled={page === 1}
              onClick={() => onPageChange?.(page - 1)}
              className={TABLE_STYLES.navButton}
            >
              <LuChevronLeft className="h-5 w-5" />
            </button>

            <div className="flex items-center gap-1">
              {generatePageNumbers(page, totalPages).map((p, index) => (
                <React.Fragment key={index}>
                  {p === "..." ? (
                    <span className="px-2 text-[#929292]">...</span>
                  ) : (
                    <button
                      onClick={() => onPageChange?.(p as number)}
                      className={cn(
                        TABLE_STYLES.pageButton,
                        page === p ? TABLE_STYLES.pageButtonActive : TABLE_STYLES.pageButtonInactive
                      )}
                    >
                      {p}
                    </button>
                  )}
                </React.Fragment>
              ))}
            </div>

            <button
              disabled={to >= total}
              onClick={() => onPageChange?.(page + 1)}
              className={TABLE_STYLES.navButton}
            >
              <LuChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export { CommonTable };
